import React from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/StatusBadge';
import { PlatformIcon } from '../components/common/PlatformIcon';
import { accountsApi, postsApi, mediaApi } from '../lib/api';
import { Plus, FileText, Users, Image, CalendarDays, Send, ArrowRight } from 'lucide-react';

export default function CompanyOverview() {
  const { company, companyId } = useOutletContext();
  const navigate = useNavigate();

  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['accounts', companyId],
    queryFn: () => accountsApi.list(companyId).then((r) => r.data),
  });

  const { data: postsData, isLoading: loadingPosts } = useQuery({
    queryKey: ['posts', companyId],
    queryFn: () => postsApi.list(companyId, { limit: 5 }).then((r) => r.data),
  });

  const { data: mediaData, isLoading: loadingMedia } = useQuery({
    queryKey: ['media', companyId],
    queryFn: () => mediaApi.list(companyId, { limit: 6 }).then((r) => r.data),
  });

  const draftCount = postsData?.posts?.filter((p) => p.status === 'draft').length || 0;
  const scheduledCount = postsData?.posts?.filter((p) => p.status === 'scheduled').length || 0;
  const publishedCount = postsData?.posts?.filter((p) => p.status === 'published').length || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Company Info Card */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold shrink-0"
              style={{ backgroundColor: company?.brand_color || '#0d9488' }}
            >
              {company?.name?.charAt(0)}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold font-display">{company?.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{company?.description || 'No description'}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span>Timezone: {company?.timezone || 'Europe/Istanbul'}</span>
                <span>Slug: {company?.slug}</span>
              </div>
            </div>
            <Button onClick={() => navigate(`/companies/${companyId}/posts/new`)} data-testid="quick-create-post">
              <Plus className="w-4 h-4 mr-2" /> New Post
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/companies/${companyId}/accounts`)}>
          <CardContent className="p-4">
            <Users className="w-4 h-4 text-blue-500 mb-2" />
            <p className="text-2xl font-semibold font-display tabular-nums">
              {loadingAccounts ? <Skeleton className="h-7 w-8" /> : accountsData?.accounts?.length || 0}
            </p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Accounts</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/companies/${companyId}/posts?status=draft`)}>
          <CardContent className="p-4">
            <FileText className="w-4 h-4 text-gray-500 mb-2" />
            <p className="text-2xl font-semibold font-display tabular-nums">{draftCount}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Drafts</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/companies/${companyId}/calendar`)}>
          <CardContent className="p-4">
            <CalendarDays className="w-4 h-4 text-sky-500 mb-2" />
            <p className="text-2xl font-semibold font-display tabular-nums">{scheduledCount}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Scheduled</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/companies/${companyId}/posts?status=published`)}>
          <CardContent className="p-4">
            <Send className="w-4 h-4 text-emerald-500 mb-2" />
            <p className="text-2xl font-semibold font-display tabular-nums">{publishedCount}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Published</p>
          </CardContent>
        </Card>
      </div>

      {/* Accounts & Posts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Connected Accounts */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-display">Connected Accounts</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/companies/${companyId}/accounts`)}>
                Manage <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingAccounts ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
            ) : accountsData?.accounts?.length > 0 ? (
              <div className="space-y-2">
                {accountsData.accounts.map((acc) => (
                  <div key={acc._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                    <PlatformIcon platform={acc.platform} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{acc.display_name}</p>
                      <p className="text-xs text-muted-foreground">{acc.username}</p>
                    </div>
                    <StatusBadge status={acc.status} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No accounts connected</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Posts */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-display">Recent Posts</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/companies/${companyId}/posts`)}>
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPosts ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : postsData?.posts?.length > 0 ? (
              <div className="space-y-2">
                {postsData.posts.map((post) => (
                  <button
                    key={post._id}
                    onClick={() => navigate(`/companies/${companyId}/posts/${post._id}/edit`)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted text-left transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{post.title || post.caption?.substring(0, 40)}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {post.platform_targets?.slice(0, 3).map((p) => (
                          <PlatformIcon key={p} platform={p} size="xs" />
                        ))}
                      </div>
                    </div>
                    <StatusBadge status={post.status} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">No posts yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Media Preview */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-display">Media Library</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/companies/${companyId}/media`)}>
              View all <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingMedia ? (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="aspect-square rounded-lg" />)}
            </div>
          ) : mediaData?.media?.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {mediaData.media.map((item) => (
                <div key={item._id} className="aspect-square rounded-lg bg-muted overflow-hidden">
                  {item.file_type === 'image' ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${item.path_or_url}`}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No media files yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
